/**
 * GeminiProvider — real LLM classification and drill-downs.
 *
 * Enabled when a Gemini API key is present (runtime Settings key, or
 * VITE_GEMINI_API_KEY at build time for development).
 *
 * PRODUCTION PATH: the backend relay (functions/index.js → Vertex AI) is
 * live — set VITE_AI_RELAY_URL and no credential ships client-side; auth
 * is a Firebase ID token per request. The direct-key path below remains
 * for local development only. See docs/RELAY_SETUP.md.
 */

import { ConsentRequiredError } from './aiConsent';
import type {
  ActionStep,
  AiProvider,
  ClassifiedTask,
  ScanAttachment,
  ZoneId,
} from '../types';
import { ZONE_BY_ID } from '../types';
import { classifyDump, toZone } from './classifier';
import { generateSteps } from './steps';
import { fallbackRevision } from './planUpdate';
import { AI_TIMEOUTS, withTimeout } from './timeout';

/**
 * Alias tracking Google's current flash model — survives model retirements.
 * The free tier intermittently returns 503 "high demand" on the primary
 * alias, so we fail over to the lite alias (verified live: lite stays up
 * while flash is overloaded) and retry once before giving up.
 */
const MODEL_CHAIN = ['gemini-flash-latest', 'gemini-flash-lite-latest'];

const CLASSIFY_SYSTEM = `You are a deterministic task-classification engine for a priority-matrix app.
Split the user's unstructured brain dump into individual tasks and assign each to exactly one zone:
- zone1: Critical & Immediate (crises, hard deadlines, breakage)
- zone2: Strategic & Growth (high-value, preventive, planning, health, learning)
- zone3: Distractions & Demands (interruptions, errands, other people's urgency)
- zone4: Time Drains & Diversions (procrastination loops, mindless consumption)
Also score urgency and importance 0-100 and echo any deadline phrase you detect.
Explicit calendar references — weekday+date ("Mon 20th"), dates ("21st July"), or
times ("9:30 am Wed") — are hard COMMITMENTS, not soft hints: weight urgency high
(dated items due within the week belong in zone1 when also important) and always
echo the date phrase as the deadline.
If an image is attached, it is a photo of a handwritten list, whiteboard, or sticky notes —
transcribe every task you can read from it and classify those too.
Handwritten pages are usually OUTLINES, not flat lists. Read the structure carefully:
- A heading line (often underlined, a project/company/area name, often ending in ".")
  is a PROJECT HEADER, not a task. NEVER output a bare header as its own task.
- Each checkbox/bullet item beneath a header is ONE task. Title it "Header: item text"
  (e.g. "Anchor & Align: Resolve App Conflicts") so sibling tasks stay visibly grouped.
- Deeper-indented dash lines are DETAILS of the item above, not separate tasks — fold
  them into that item's text in parentheses
  (e.g. "Anchor & Align: Resolve App Conflicts (Belief Module, Coherence Index)").
- An un-headed item at the top level is a task on its own, with no prefix.
- A line that is TICKED (checked box), crossed out, or struck through is COMPLETED
  work. Include it with alreadyDone=true. NEVER silently drop it and NEVER import
  it as an open task.
- A handwritten urgency annotation (the word URGENT, a boxed or starred group,
  "don't ignore") marks the MOST IMPORTANT content on the page. Those items must
  ALWAYS be captured — missing one is the worst possible error — with high urgency
  and zone1.
- The photo may be rotated 90° or 180° — detect the writing's orientation first and
  read it the right way up.
- Preserve the writer's spelling of proper names; when handwriting is ambiguous,
  prefer real words over gibberish.
Respond with JSON only.`;

const BATCH_STICKY_HINT =
  'The image contains multiple independent sticky notes. Parse each sticky note as a separate, discrete task.';

/**
 * Two rules every plan prompt carries (18 Oct 2026, MEASURED on Chad's board):
 * the model copied the internal id "zone1" into step text ("open the screen
 * for zone1", "locate the target zone1 area"), and read a leading "Chad:"
 * label as part of the job ("Chad: Lifeline Expose" became a covert
 * operation). The zone is now sent by name (zoneForPrompt) AND the model is
 * told what both lines are.
 */
const PLAN_CONTEXT_RULES = `The "Zone" line is background on urgency and importance only — never mention zones, zone names or zone numbers in a step.
A leading label followed by a colon (for example "Chad:" or "Acme Project:") names the person or project the task belongs to; it is not part of the action. Plan the rest of the task text literally and plainly.`;

/** The zone as words, never the internal id. */
export function zoneForPrompt(zone: ZoneId): string {
  const meta = ZONE_BY_ID[zone];
  return meta ? `${meta.title} — ${meta.subtitle}` : 'unspecified';
}

const EXPAND_SYSTEM = `You are an execution coach. Break the given task into 3-5 concrete,
immediately actionable steps, each with an estimated duration in minutes.
Steps must be specific to the task, start with a verb, and be completable in one sitting.
${PLAN_CONTEXT_RULES}
Respond with JSON only.`;

const REVISE_SYSTEM = `You are an execution coach maintaining an EXISTING action plan for a task.
You receive the task, its COMPLETED steps (context only — work already done), its remaining
OPEN steps, and the user's update note describing what changed (meeting notes, a decision,
a setback, new requirements).
Return the revised list of remaining OPEN steps only:
- NEVER include, rewrite, or duplicate a completed step. Completed steps are history; the app
  preserves them and will reject any you echo back.
- Keep open steps that are still relevant (verbatim when unchanged), drop ones the update
  makes obsolete, adjust ones it changes, and add the new steps it requires — in sensible
  execution order.
- 1-8 steps, each starting with a verb, each with an estimated duration in minutes.
${PLAN_CONTEXT_RULES}
Respond with JSON only.`;

interface GeminiOptions {
  apiKey: string;
  /** Override for backend relay deployments. */
  endpoint?: string;
  /**
   * Auth for relay mode (Firebase ID token). Injected by the provider
   * factory so this engine module stays free of app/framework imports.
   */
  getAuthToken?: () => Promise<string | null>;
  /**
   * Consent gate at the NETWORK layer (17 Oct 2026, App Review 5.1.2(i)):
   * when provided and false, no request is built and no token is fetched —
   * ConsentRequiredError instead. The factory always provides it; a bare
   * construction (tests) is treated as consented.
   */
  hasConsent?: () => boolean;
}

type GeminiPart =
  | { text: string }
  | { inlineData: { mimeType: string; data: string } };

export class GeminiProvider implements AiProvider {
  readonly name = 'Gemini Flash (latest)';
  readonly supportsVision = true;
  private readonly apiKey: string;
  /** Custom relay endpoint — when set, the model chain is the relay's job. */
  private readonly endpointOverride?: string;
  private readonly getAuthToken?: () => Promise<string | null>;
  private readonly hasConsent?: () => boolean;
  /** Offline/error fallback so the product never dead-ends. */
  private readonly fallbackNote = 'Gemini unavailable — used local engine fallback';
  lastError?: string;

  constructor(opts: GeminiOptions) {
    this.apiKey = opts.apiKey;
    this.endpointOverride = opts.endpoint;
    this.getAuthToken = opts.getAuthToken;
    this.hasConsent = opts.hasConsent;
  }

  private endpointFor(model: string): string {
    return (
      this.endpointOverride ??
      `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`
    );
  }

  private async callOnce(
    model: string,
    system: string,
    parts: GeminiPart[],
    schema: object,
  ): Promise<unknown> {
    // Consent first — before any token, any header, any byte (5.1.2(i)).
    if (this.hasConsent && !this.hasConsent()) throw new ConsentRequiredError();
    // Relay mode: authenticate with a Firebase ID token (anonymous session
    // is fine) — no API key exists client-side. Direct mode (dev): key param.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    let url = this.endpointFor(model);
    if (this.endpointOverride) {
      // Bounded (9 Oct 2026): on iOS this await never settled and froze
      // Organize on "Structuring your week…" with no error. Now it fails
      // fast with a named step, and classify() falls back to the local engine.
      const token = this.getAuthToken
        ? await withTimeout(this.getAuthToken(), AI_TIMEOUTS.authMs, 'AI sign-in (Firebase auth token)')
        : null;
      if (!token) throw new Error('Relay auth unavailable');
      headers.Authorization = `Bearer ${token}`;
    } else {
      url += `?key=${encodeURIComponent(this.apiKey)}`;
    }
    const controller = typeof AbortController === 'undefined' ? undefined : new AbortController();
    const res = await withTimeout(
      fetch(url, {
        method: 'POST',
        headers,
        signal: controller?.signal,
        body: JSON.stringify({
        systemInstruction: { parts: [{ text: system }] },
        contents: [{ role: 'user', parts }],
        generationConfig: {
          temperature: 0.2,
          responseMimeType: 'application/json',
          responseSchema: schema,
        },
      }),
      }),
      AI_TIMEOUTS.requestMs,
      'AI request',
    ).catch((err: unknown) => {
      controller?.abort();
      throw err;
    });
    if (!res.ok) throw new Error(`Gemini HTTP ${res.status}${await relayDetail(res)}`);
    const data = (await res.json()) as {
      candidates?: { content?: { parts?: { text?: string }[] } }[];
    };
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!text) throw new Error('Gemini returned an empty response');
    return JSON.parse(text) as unknown;
  }

  /**
   * Overload-resilient call: walk the model chain (primary → lite), and if
   * every model hit a transient error (429/5xx/network), wait briefly and
   * try the chain once more. Non-transient errors (bad request, bad key)
   * throw immediately.
   */
  private async call(system: string, parts: GeminiPart[], schema: object): Promise<unknown> {
    const chain = this.endpointOverride ? [MODEL_CHAIN[0]] : MODEL_CHAIN;
    let lastErr = new Error('Gemini unavailable');
    for (let round = 0; round < 2; round += 1) {
      if (round > 0) await new Promise((r) => setTimeout(r, 1500));
      for (const model of chain) {
        try {
          return await this.callOnce(model, system, parts, schema);
        } catch (err) {
          lastErr = err as Error;
          if (!isTransient(lastErr)) throw lastErr;
        }
      }
    }
    throw lastErr;
  }

  async classify(rawInput: string, scans?: ScanAttachment[]): Promise<ClassifiedTask[]> {
    try {
      const schema = {
        type: 'object',
        properties: {
          tasks: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                text: { type: 'string' },
                zone: { type: 'string', enum: ['zone1', 'zone2', 'zone3', 'zone4'] },
                urgency: { type: 'integer' },
                importance: { type: 'integer' },
                deadlineHint: { type: 'string' },
                alreadyDone: { type: 'boolean' },
              },
              required: ['text', 'zone', 'urgency', 'importance'],
            },
          },
        },
        required: ['tasks'],
      };
      const parts: GeminiPart[] = [];
      if (scans?.some((s) => s.batchStickyMode)) parts.push({ text: BATCH_STICKY_HINT });
      if (rawInput.trim()) parts.push({ text: rawInput });
      for (const s of scans ?? []) {
        parts.push({ inlineData: { mimeType: s.mimeType, data: s.dataBase64 } });
      }
      if (parts.length === 0) return [];
      const raw = (await this.call(CLASSIFY_SYSTEM, parts, schema)) as {
        tasks?: Partial<ClassifiedTask>[];
      };
      const tasks = (raw.tasks ?? [])
        .filter((t): t is ClassifiedTask => typeof t.text === 'string' && t.text.length > 0)
        .map((t) => ({
          text: t.text,
          urgency: clampScore(t.urgency),
          importance: clampScore(t.importance),
          zone: isZone(t.zone) ? t.zone : toZone(clampScore(t.urgency), clampScore(t.importance)),
          deadlineHint: t.deadlineHint || undefined,
          alreadyDone: t.alreadyDone === true || undefined,
        }));
      if (tasks.length === 0) throw new Error('Gemini returned no tasks');
      this.lastError = undefined;
      return tasks;
    } catch (err) {
      this.lastError = `${this.fallbackNote}: ${(err as Error).message}`;
      return classifyDump(rawInput);
    }
  }

  async expand(
    taskText: string,
    zone: ZoneId,
  ): Promise<Omit<ActionStep, 'id' | 'done'>[]> {
    try {
      const schema = {
        type: 'object',
        properties: {
          steps: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                text: { type: 'string' },
                minutes: { type: 'integer' },
              },
              required: ['text', 'minutes'],
            },
          },
        },
        required: ['steps'],
      };
      const raw = (await this.call(
        EXPAND_SYSTEM,
        [{ text: `Task: ${taskText}\nZone: ${zoneForPrompt(zone)}` }],
        schema,
      )) as { steps?: { text?: string; minutes?: number }[] };
      const steps = (raw.steps ?? [])
        .filter((s) => typeof s.text === 'string' && s.text.length > 0)
        .map((s) => ({ text: s.text as string, minutes: clampMinutes(s.minutes) }));
      if (steps.length === 0) throw new Error('Gemini returned no steps');
      this.lastError = undefined;
      return steps;
    } catch (err) {
      this.lastError = `${this.fallbackNote}: ${(err as Error).message}`;
      return generateSteps(taskText, zone);
    }
  }

  async revise(
    taskText: string,
    zone: ZoneId,
    completed: ActionStep[],
    open: ActionStep[],
    note: string,
  ): Promise<Omit<ActionStep, 'id' | 'done'>[]> {
    try {
      const schema = {
        type: 'object',
        properties: {
          steps: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                text: { type: 'string' },
                minutes: { type: 'integer' },
              },
              required: ['text', 'minutes'],
            },
          },
        },
        required: ['steps'],
      };
      const describe = (steps: ActionStep[]) =>
        steps.length === 0
          ? '(none)'
          : steps.map((s) => `- ${s.text} (${s.minutes} min)`).join('\n');
      const raw = (await this.call(
        REVISE_SYSTEM,
        [
          {
            text:
              `Task: ${taskText}\nZone: ${zoneForPrompt(zone)}\n\n` +
              `COMPLETED steps (context only — never return these):\n${describe(completed)}\n\n` +
              `Remaining OPEN steps:\n${describe(open)}\n\n` +
              `Update note from the user:\n${note}`,
          },
        ],
        schema,
      )) as { steps?: { text?: string; minutes?: number }[] };
      const steps = (raw.steps ?? [])
        .filter((s) => typeof s.text === 'string' && s.text.length > 0)
        .map((s) => ({ text: s.text as string, minutes: clampMinutes(s.minutes) }));
      if (steps.length === 0) throw new Error('Gemini returned no steps');
      this.lastError = undefined;
      return steps;
    } catch (err) {
      this.lastError = `${this.fallbackNote}: ${(err as Error).message}`;
      return fallbackRevision(open, note);
    }
  }
}

function clampScore(n: unknown): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : 50;
  return Math.max(0, Math.min(100, Math.round(v)));
}

function clampMinutes(n: unknown): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : 10;
  return Math.max(1, Math.min(240, Math.round(v)));
}

function isZone(z: unknown): z is ZoneId {
  return z === 'zone1' || z === 'zone2' || z === 'zone3' || z === 'zone4';
}

/**
 * The relay's one-line reason (`detail`, else `error`) for a non-OK answer —
 * a safety block (422) or the exhausted model chain (503) — so the board
 * can show WHY instead of a bare status (CLAUDE.md §2). Never throws;
 * capped so a payload echo cannot flood the UI. Format: " — <reason>".
 */
async function relayDetail(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { detail?: unknown; error?: unknown };
    const d = typeof j.detail === 'string' ? j.detail : typeof j.error === 'string' ? j.error : '';
    const t = d.replace(/\s+/g, ' ').trim();
    return t ? ` — ${t.length > 200 ? `${t.slice(0, 199)}…` : t}` : '';
  } catch {
    return '';
  }
}

/** Worth retrying / failing over: rate limits, server overload, network. */
function isTransient(err: Error): boolean {
  const status = /HTTP (\d+)/.exec(err.message)?.[1];
  if (status) return status === '429' || status.startsWith('5');
  // fetch() network failures (offline, DNS, CORS hiccup) surface as TypeError.
  return err.name === 'TypeError' || /empty response/.test(err.message);
}
